import React, { useMemo } from 'react'
import { ControlledSelect, ControlledTextInput } from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import {
  IInvoice,
  ITransactionSetTypes,
  IConfigurableBuilderTest,
  ITransactionSetSubtypes,
  ITransactionSetWithSubtype,
} from 'types/graphqlTypes'
import { BuilderInvoicesForm } from '../forms/BuilderInvoicesForm'
import { TBuilderInvoicesFormSchema } from '../forms/BuilderInvoicesForm/BuilderInvoicesFormSchema'
import { useConfigurableBuilderInvoicesLazyQuery } from '../graphql/ConfigurableBuilderInvoices'
import { useOutboundInputOptionsForm } from '../hooks/useOutboundInputOptionsForm'

type BuilderInvoice = Pick<IInvoice, 'id' | 'name'>

interface IOutboundInputOptionsModalProps extends Omit<IModalBaseProps, 'modalType'> {
  transactionSetWithSubtype: ITransactionSetWithSubtype
  options: IConfigurableBuilderTest
  onCancel: () => void
  onSubmit: (options: IConfigurableBuilderTest) => void
}

const useOutboundInputOptionsModalStyles = makeStyles({
  body: {},
  modal: {},
  header: {},
  title: {},
  root: {},
})

export const OutboundInputOptionsModal = ({
  onCancel,
  onSubmit,
  open = true,
  options,
  transactionSetWithSubtype,
  ...props
}: IOutboundInputOptionsModalProps) => {
  const methods = useOutboundInputOptionsForm()
  const { control, errors, handleSubmit } = methods

  const submitHandler = handleSubmit((formParams: IConfigurableBuilderTest) => {
    onSubmit(formParams)
    onCancel()
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onCancel()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const classes = useOutboundInputOptionsModalStyles()

  const [
    getBuilderInvoices,
    { data, loading: loadingInvoices },
  ] = useConfigurableBuilderInvoicesLazyQuery()

  const { invoices: builderInvoices = [] } = data ?? {}

  const shipmentIdSubmitHandler = (formData: TBuilderInvoicesFormSchema) => {
    getBuilderInvoices({
      variables: {
        shipmentId: formData.shipmentId || '',
      },
    })
  }

  const builderInvoiceOptions = useMemo(
    () =>
      builderInvoices.map((value: BuilderInvoice) => {
        return {
          value: value.id,
          label: value.name,
        }
      }),
    [builderInvoices]
  )

  const getModalFields = (transactionSetWithSubtype: ITransactionSetWithSubtype) => {
    switch (transactionSetWithSubtype.transactionSet) {
      case ITransactionSetTypes._990: {
        return (
          <Box mb={2}>
            <ControlledTextInput
              control={control}
              name='shipmentId'
              defaultValue={options?.shipmentId}
              TextInputProps={{
                label: 'Shipment ID',
                required: true,
                large: true,
                validationMessage: errors?.shipmentId?.message,
                error: !!errors?.shipmentId?.message,
              }}
            />
          </Box>
        )
      }
      case ITransactionSetTypes._210: {
        return (
          <>
            <Box mb={2}>
              <BuilderInvoicesForm onSubmit={shipmentIdSubmitHandler} loading={loadingInvoices} />
            </Box>
            <Box mb={2}>
              {!loadingInvoices && (
                <ControlledSelect
                  control={control}
                  name='accountingInvoiceId'
                  defaultValue=''
                  SelectProps={{
                    label: 'Accounting Invoice Name',
                    options: builderInvoiceOptions,
                    large: true,
                  }}
                />
              )}
            </Box>
          </>
        )
      }
      case ITransactionSetTypes._214: {
        return (
          <>
            <Box mb={2}>
              <ControlledTextInput
                control={control}
                name='shipmentId'
                defaultValue={options?.shipmentId}
                TextInputProps={{
                  label: 'Shipment ID',
                  required: true,
                  large: true,
                  validationMessage: errors?.shipmentId?.message,
                  error: !!errors?.shipmentId?.message,
                }}
              />
            </Box>
            {transactionSetWithSubtype.transactionSetSubtype !==
              ITransactionSetSubtypes.SCHEDULED_TRACKING && (
              <>
                <Box mb={2}>
                  <ControlledTextInput
                    control={control}
                    name='changedField'
                    defaultValue={options?.changedField}
                    TextInputProps={{
                      label: 'Changed Field',
                      required: true,
                      large: true,
                    }}
                  />
                </Box>
                <Box mb={2}>
                  <ControlledTextInput
                    control={control}
                    name='changedShipperStopSequence'
                    defaultValue={options?.changedShipperStopSequence}
                    TextInputProps={{
                      label: 'Changed Shipper Stop Sequence',
                      required: true,
                      large: true,
                    }}
                  />
                </Box>
              </>
            )}
          </>
        )
      }
      default: {
        return <Box>No inputs configured for this message type.</Box>
      }
    }
  }

  return (
    <ModalBase
      dataTest='OutboundInputOptionsModal'
      modalType={MODAL_TYPES.form}
      size={4}
      open={open}
      actions={actions}
      className={classes.root}
      bodyClassName={classes.body}
      titleClass={classes.title}
      modalClassName={classes.modal}
      headerClassName={classes.header}
      title='Outbound Input Options'
      {...props}>
      {options.id && (
        <>
          Currently using test
          <Typography variant='h4' paragraph>
            {options.name}
          </Typography>
          <hr />
        </>
      )}
      {getModalFields(transactionSetWithSubtype)}
    </ModalBase>
  )
}
